import { ChangeEvent, ReactNode } from "react";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../utils";
import { ExpressTotals } from "./totals";

interface TotalsBlockProps {
  totals: ExpressTotals;
  vatRate: number;
  /** False on price type 0 (ไม่มี VAT): the base and VAT rows would only repeat the total and 0.00. */
  hasVat: boolean;
  /** Keyed on R5 / P4 while the document is edited; shown as stored otherwise. */
  discount: {
    text: string;
    isEditable: boolean;
    error?: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  } | null;
}

const DISCOUNT_INPUT_WIDTH = 140;
const NUMERIC_INPUT = { htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } } as const;

export function TotalsRow({
  label,
  children,
  isGrandTotal = false,
}: Readonly<{ label: string; children: ReactNode; isGrandTotal?: boolean }>) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} py={0.5}>
      <Typography variant={isGrandTotal ? "subtitle1" : "body2"} fontWeight={isGrandTotal ? 700 : 400}>
        {label}
      </Typography>
      <Typography
        component="div"
        variant={isGrandTotal ? "h6" : "body2"}
        fontWeight={isGrandTotal ? 700 : 500}
        color={isGrandTotal ? "primary" : "text.primary"}
      >
        {children}
      </Typography>
    </Stack>
  );
}

/** The totals at the bottom right of the Express screen, in its order. */
export default function TotalsBlock({
  totals,
  vatRate,
  hasVat,
  discount,
}: Readonly<TotalsBlockProps>) {
  const discountValue = discount?.isEditable ? (
    <TextField
      size="small"
      value={discount.text}
      placeholder="0.00"
      onChange={discount.onChange}
      error={Boolean(discount.error)}
      helperText={discount.error}
      sx={{ width: DISCOUNT_INPUT_WIDTH }}
      slotProps={NUMERIC_INPUT}
    />
  ) : (
    formatNumber(totals.discountAmount)
  );

  return (
    <Stack>
      <TotalsRow label="จำนวนเงิน">{formatNumber(totals.subtotal)}</TotalsRow>
      {discount && <TotalsRow label="หักส่วนลด">{discountValue}</TotalsRow>}
      {hasVat && (
        <>
          <TotalsRow label="มูลค่าก่อนภาษี">{formatNumber(totals.baseAmount)}</TotalsRow>
          <TotalsRow label={`ภาษีมูลค่าเพิ่ม ${formatNumber(vatRate)}%`}>
            {formatNumber(totals.vatAmount)}
          </TotalsRow>
        </>
      )}
      <Divider sx={{ my: 1 }} />
      <TotalsRow label="จำนวนเงินรวมทั้งสิ้น" isGrandTotal>
        {formatNumber(totals.netAmount)}
      </TotalsRow>
    </Stack>
  );
}
